/**
 * Video-votings resource: the votings defined in this silo, one voting by key,
 * and the editorial writes that create and replace them.
 *
 * The reads are public, because the player serves signed-out viewers, so
 * neither hook gates on a session.
 *
 * Both writes send the whole voting, matching the contract — the editor holds
 * the voting it last read and saves it back — and invalidate the two reads that
 * could now be stale.
 */
import type { SpaceVotingResults, VideoVote, VideoVoting, VideoVotingWriteInput } from '@repo/shared/orpc/procedures/video-votings';
import { useApiQueryUtils } from 'system/api-client-context';
import { type Datastore } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import { fetchProfileAsync } from './profile';
import { siloParamsOf } from './silo-params';
import { useSiloQuery } from './use-silo-query';

export type {
    SpaceVotingResults,
    VideoVote,
    VideoVoting,
    VideoVotingWriteInput,
} from '@repo/shared/orpc/procedures/video-votings';

// One shared empty list, so a still-loading read returns a stable reference
// rather than a fresh array every render.
const NO_VOTINGS: VideoVoting[] = [];

/** Every video voting in the silo. Empty while loading. */
export function useAllVideoVotings(): { votings: VideoVoting[]; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.videoVotings.list });
    return { votings: query.data?.votings ?? NO_VOTINGS, isLoading: query.isLoading };
}

/** One video voting by key, or undefined while loading and when none exists. */
export function useVideoVoting(votingKey: string | null | undefined): VideoVoting | undefined {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.videoVotings.get, input: { votingKey: votingKey ?? '' }, enabled: !!votingKey });
    return query.data;
}

/**
 * The votings attached to one story. A story carries at most one, so callers
 * take the first entry rather than rendering a list.
 */
export function useVideoVotingsForStory(storyKey: string | null | undefined): { votings: VideoVoting[]; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.videoVotings.listForStory, input: { storyKey: storyKey ?? '' }, enabled: !!storyKey });
    return { votings: query.data?.votings ?? NO_VOTINGS, isLoading: query.isLoading };
}

async function invalidateStoryAsync(datastore: Datastore, storyKey: string): Promise<void> {
    const { siloKey } = siloParamsOf(datastore);
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    await queryClient.invalidateQueries({
        queryKey: utils.videoVotings.listForStory.queryOptions({ input: { siloKey, storyKey } }).queryKey,
    });
}

/** Attaches a voting to a story, so its prompts play with that article. */
export async function assignVideoVotingAsync(datastore: Datastore, { votingKey, storyKey }: AssignmentInput): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    await datastore.getApiClient().videoVotings.assign({ siloKey, locale, votingKey, storyKey });
    await invalidateStoryAsync(datastore, storyKey);
}

/** Detaches a voting from a story, leaving the votes already cast in place. */
export async function unassignVideoVotingAsync(datastore: Datastore, { votingKey, storyKey }: AssignmentInput): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    await datastore.getApiClient().videoVotings.unassign({ siloKey, locale, votingKey, storyKey });
    await invalidateStoryAsync(datastore, storyKey);
}

type AssignmentInput = { votingKey: string; storyKey: string };

const NO_VOTES: VideoVote[] = [];
const NO_RESULTS: SpaceVotingResults[] = [];

/**
 * Everything a space's Votings tab renders: its linked stories, each with the
 * voting attached to it and every vote cast. One read where the tab used to
 * join four.
 */
export function useVideoVotingResultsForSpace(spaceKey: string | null | undefined): {
    results: SpaceVotingResults[];
    isLoading: boolean;
} {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.videoVotings.listForSpace, input: { spaceKey: spaceKey ?? '' }, enabled: !!spaceKey });
    return { results: query.data?.results ?? NO_RESULTS, isLoading: query.isLoading };
}

/** Every vote cast on a voting, across all of its prompts. */
export function useVideoVotes(votingKey: string | null | undefined): { votes: VideoVote[]; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.videoVotings.listVotes, input: { votingKey: votingKey ?? '' }, enabled: !!votingKey });
    return { votes: query.data?.votes ?? NO_VOTES, isLoading: query.isLoading };
}

type VotesCache = { votes: VideoVote[] };

/**
 * Patches the cached vote list, then reconciles with the server.
 *
 * The patch is what keeps the dialog responsive. A viewer answers a prompt
 * against a countdown, and before the port the count moved the instant they
 * pressed, because the write updated the local datastore first. Waiting for a
 * round trip would make every press feel late, so the expected result is
 * written into the cache and rolled back if the server refuses.
 */
async function patchVotesAsync(
    datastore: Datastore,
    votingKey: string,
    update: (votes: VideoVote[]) => VideoVote[],
    write: () => Promise<void>,
): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    const votesKey = utils.videoVotings.listVotes.queryOptions({ input: { siloKey, locale, votingKey } }).queryKey;

    await queryClient.cancelQueries({ queryKey: votesKey });
    const previous = queryClient.getQueryData<VotesCache>(votesKey);
    queryClient.setQueryData<VotesCache>(votesKey, { votes: update(previous?.votes ?? []) });

    try {
        await write();
    } catch (err) {
        queryClient.setQueryData(votesKey, previous);
        throw err;
    } finally {
        fireAndForget(queryClient.invalidateQueries({ queryKey: votesKey }));
    }
}

type SetVoteInput = { votingKey: string; questionKey: string; optionKey?: string | null; value?: number | null; anonymous?: boolean };

/** Casts or changes the caller's vote on one prompt. */
export async function setVideoVoteAsync(datastore: Datastore, input: SetVoteInput): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const myKey = datastore.getPersonaKey();
    const { votingKey, questionKey, optionKey = null, value = null, anonymous = false } = input;

    const optimistic: VideoVote = {
        voteKey: `pending-${questionKey}`,
        questionKey,
        optionKey,
        value,
        anonymous,
        voter: anonymous ? null : await fetchProfileAsync(datastore, myKey),
        mine: true,
    };

    await patchVotesAsync(
        datastore,
        votingKey,
        (votes) => [...votes.filter((v) => !(v.questionKey === questionKey && v.mine)), optimistic],
        async () => {
            await datastore.getApiClient().videoVotings.setVote({ siloKey, locale, ...input });
        },
    );
}

/** Removes the caller's vote on one prompt, if they cast one. */
export async function removeVideoVoteAsync(datastore: Datastore, { votingKey, questionKey }: RemoveVoteInput): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);

    await patchVotesAsync(
        datastore,
        votingKey,
        (votes) => votes.filter((v) => !(v.questionKey === questionKey && v.mine)),
        async () => {
            await datastore.getApiClient().videoVotings.removeVote({ siloKey, locale, votingKey, questionKey });
        },
    );
}

type RemoveVoteInput = { votingKey: string; questionKey: string };

/**
 * Whether the viewer has switched video-voting prompts off for every video.
 * Defaults to false while loading, so a slow read shows prompts rather than
 * hiding a feature the viewer never turned off.
 */
export function useVideoVotingDisabled(): boolean {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.videoVotings.myPreferences });
    return query.data?.votingDisabled ?? false;
}

/** Switches video-voting prompts off, or back on, for every video. */
export async function setVideoVotingDisabledAsync(datastore: Datastore, votingDisabled: boolean): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    const preferencesKey = utils.videoVotings.myPreferences.queryOptions({ input: { siloKey } }).queryKey;

    await datastore.getApiClient().videoVotings.setMyPreferences({ siloKey, locale, votingDisabled });
    await queryClient.invalidateQueries({ queryKey: preferencesKey });
}

async function invalidateVotingAsync(datastore: Datastore, votingKey: string): Promise<void> {
    const { siloKey } = siloParamsOf(datastore);
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    await Promise.all([
        queryClient.invalidateQueries({ queryKey: utils.videoVotings.list.queryOptions({ input: { siloKey } }).queryKey }),
        queryClient.invalidateQueries({ queryKey: utils.videoVotings.get.queryOptions({ input: { siloKey, votingKey } }).queryKey }),
    ]);
}

/** Creates a voting and returns it, including the key the server assigned. */
export async function createVideoVotingAsync(datastore: Datastore, input: VideoVotingWriteInput): Promise<VideoVoting> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const created = await datastore.getApiClient().videoVotings.create({ siloKey, locale, ...input });
    await invalidateVotingAsync(datastore, created.votingKey);
    return created;
}

/** Replaces a voting wholesale, `name` included, returning what was stored. */
export async function editVideoVotingAsync(
    datastore: Datastore,
    { votingKey, ...input }: VideoVotingWriteInput & { votingKey: string },
): Promise<VideoVoting> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const saved = await datastore.getApiClient().videoVotings.edit({ siloKey, locale, votingKey, ...input });
    await invalidateVotingAsync(datastore, votingKey);
    return saved;
}
